import { createContext, useContext, useEffect, useState } from 'react'

import {
  useKnockClient,
  useNotifications,
  useNotificationStore,
} from '@knocklabs/react'
import {
  IconArchive,
  IconArchiveOff,
  IconBell,
  IconCheckbox,
  IconDotsVertical,
  IconFilter,
  IconInbox,
} from '@tabler/icons-react'
import { Tooltip } from 'antd'
import { formatDistanceToNowStrict, format } from 'date-fns'
import { Interweave } from 'interweave'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { Avatar } from '../shared-components/Avatar'
import { Button } from '../shared-components/BUI/Button'
import { Popover, PopoverItem } from '../shared-components/BUI/Popover'
import { Text } from '../shared-components/BUI/Text'
import { DozerLoading } from '../shared-components/DozerLoading'
import { Flexbox } from '../shared-components/Flexbox'
import { ResponsiveDrawer } from '../shared-components/ResponsiveDrawer'

import config from '@/config/index'
import { pluraliseWord } from '@/utils/pluralise'

import { buildPassTheme } from 'src/styles/theme'

export const Count = styled.div`
  ${({ theme }) => theme.font.caption01}
  font-size: 10px;
  background: ${({ theme }) => theme.colour.semantic.error};
  color: ${({ theme }) => theme.colour.neutral.white};
  border-radius: 999px;
  padding: 0.125rem 0.375rem;
  margin-left: auto;
`

const NoShrink = styled.div`
  flex-shrink: 0;
`

const AvatarWrapper = styled(NoShrink)`
  position: relative;
`

const Unread = styled.div`
  position: absolute;
  top: -0.75rem;
  left: -0.25rem;
  background: ${({ theme }) => theme.colour.semantic.error};
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  margin-top: 0.5rem;
`

const Content = styled.div`
  margin-right: 2rem;
`

const ItemActions = styled(Flexbox)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: ${({ theme }) => theme.colour.neutral.white};
  border: 1px solid ${({ theme }) => theme.colour.neutral.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: none;
`

const Notification = styled(Flexbox)`
  position: relative;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colour.neutral.border};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colour.neutral.surface};

    & ${Content} {
      text-decoration: underline;
    }

    & ${ItemActions} {
      display: flex;
    }
  }
`

const Distance = styled(NoShrink)`
  margin-left: auto;
  color: ${({ theme }) => theme.colour.secondary._400};
`

const getDistance = (dateStr: string) => {
  const date = new Date(dateStr)
  const distanceChunks = formatDistanceToNowStrict(date).split(' ')

  let unit = distanceChunks[1]

  switch (unit) {
    case 'year':
    case 'years': {
      unit = 'y'
      break
    }
    case 'month':
    case 'months': {
      return format(date, 'MMM d')
    }
    case 'day':
    case 'days': {
      unit = 'd'
      break
    }
    case 'hour':
    case 'hours': {
      unit = 'h'
      break
    }

    case 'minute':
    case 'minutes': {
      unit = 'm'
      break
    }
    case 'second':
    case 'seconds': {
      unit = 's'
      break
    }
  }

  return distanceChunks[0] + unit
}

type InboxContextValue = {
  totalCount: number
  unreadCount: number
  items: ReturnType<typeof useNotificationStore>['items']
  drawerOpen: boolean
  setDrawerOpen: (open: boolean) => void
  feedClient: ReturnType<typeof useNotifications>
  archived: boolean
  setArchived: (archived: boolean) => void
  loading: boolean
}

const InboxContext = createContext<InboxContextValue>({
  totalCount: 0,
  unreadCount: null,
  items: [],
  drawerOpen: false,
  setDrawerOpen: () => {},
  feedClient: null,
  archived: false,
  setArchived: () => {},
  loading: false,
})

export const InboxProvider = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [archived, setArchived] = useState(false)
  const [loading, setLoading] = useState(false)

  const knockClient = useKnockClient()
  const feedClient = useNotifications(knockClient, config.knock.feedId, {
    archived: archived ? 'only' : 'exclude',
  })

  const { items, metadata } = useNotificationStore(feedClient)

  useEffect(() => {
    const getNotifications = async () => {
      setLoading(true)
      await feedClient.fetch()
      setLoading(false)
    }

    getNotifications()
  }, [feedClient])

  return (
    <InboxContext.Provider
      value={{
        drawerOpen,
        setDrawerOpen,
        items,
        totalCount: metadata.total_count,
        unreadCount: metadata.unread_count,
        feedClient,
        archived,
        setArchived,
        loading,
      }}
    >
      {children}
    </InboxContext.Provider>
  )
}

export const useInboxProvider = () => useContext(InboxContext)

export const InboxUserMenuItem: React.FC = () => {
  const { unreadCount, setDrawerOpen } = useInboxProvider()

  return (
    <>
      <PopoverItem as="span" onClick={() => setDrawerOpen(true)}>
        <IconInbox size={18} />
        Inbox
        {!!unreadCount && <Count>{unreadCount}</Count>}
      </PopoverItem>
    </>
  )
}

export const InboxDrawer = () => {
  const router = useRouter()

  const inbox = useInboxProvider()

  const onClick = async (
    actionUrl: string,
    item: (typeof inbox.items)[number]
  ) => {
    inbox.feedClient.markAsRead(item)

    if (actionUrl) {
      router.push(actionUrl)
    }
  }

  return (
    <ResponsiveDrawer
      destroyOnClose
      open={inbox.drawerOpen}
      onClose={() => inbox.setDrawerOpen(false)}
      maxWidth={500}
      bodyStyle={{ padding: 0 }}
      title={
        <Flexbox justifyContent="space-between" alignItems="center">
          Inbox
          <Popover
            showArrow={false}
            content={
              <>
                <PopoverItem onClick={() => inbox.feedClient.markAllAsRead()}>
                  <IconCheckbox size={16} />
                  Mark all as read
                </PopoverItem>
                <PopoverItem
                  onClick={() => inbox.feedClient.markAllAsArchived()}
                >
                  <IconArchive size={16} />
                  Mark all as archived
                </PopoverItem>
                <PopoverItem onClick={() => inbox.setArchived(!inbox.archived)}>
                  <IconFilter size={16} />
                  {inbox.archived ? 'Show unread' : 'Show archived'}
                </PopoverItem>
              </>
            }
          >
            <Button
              icon={
                <>
                  <IconInbox size={16} />
                  <IconDotsVertical size={16} />
                </>
              }
              size="small"
              variant="secondary-text"
            />
          </Popover>
        </Flexbox>
      }
    >
      <div className="notifications">
        {!!inbox.items.length && (
          <Text
            variant="caption02"
            align="center"
            mt="1.5rem"
            mb="0.5rem"
            secondary="_400"
          >
            You have {inbox.unreadCount} unread{' '}
            {pluraliseWord(inbox.unreadCount, 'item')}
          </Text>
        )}

        {inbox.items.map((item) => {
          const actionUrl =
            item.blocks[1].type == 'text' &&
            item.blocks[1]?.name === 'action_url'
              ? item.blocks[1].rendered
              : null

          return (
            <Notification
              key={item.id}
              gap="0.5rem"
              onClick={() => onClick(actionUrl, item)}
            >
              <ItemActions alignItems="center" gap="0.5rem">
                <Tooltip
                  title="Mark this notification as read"
                  placement="topRight"
                >
                  <Button
                    size="small"
                    variant="secondary-text"
                    icon={<IconCheckbox size={16} />}
                    shape="circle"
                    onClick={(e) => {
                      e.stopPropagation()
                      inbox.feedClient.markAsRead(item)
                    }}
                  />
                </Tooltip>
                {item.archived_at ? (
                  <Tooltip
                    title="Unarchive this notification"
                    placement="topRight"
                  >
                    <Button
                      size="small"
                      variant="secondary-text"
                      icon={<IconArchiveOff size={16} />}
                      shape="circle"
                      onClick={(e) => {
                        e.stopPropagation()
                        inbox.feedClient.markAsUnarchived(item)
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip
                    title="Archive this notification"
                    placement="topRight"
                  >
                    <Button
                      size="small"
                      variant="secondary-text"
                      icon={<IconArchive size={16} />}
                      shape="circle"
                      onClick={(e) => {
                        e.stopPropagation()
                        inbox.feedClient.markAsArchived(item)
                      }}
                    />
                  </Tooltip>
                )}
              </ItemActions>

              <AvatarWrapper>
                {!item.read_at && <Unread />}
                <Avatar
                  name={item.actors?.[0]?.['name']}
                  size={28}
                  forceColour={buildPassTheme.colour.secondary._100}
                  bordered
                />
              </AvatarWrapper>

              <Content>
                {item.blocks[0]?.type === 'text' && item.blocks[0].rendered}
                {item.blocks[0]?.type === 'markdown' && (
                  <Interweave content={item.blocks[0].rendered} />
                )}
              </Content>

              <Distance>{getDistance(item.inserted_at)}</Distance>
            </Notification>
          )
        })}

        <DozerLoading
          loading={inbox.loading}
          relative
          noBackground
          size={32}
          text="Finding more notifications..."
        />

        {!inbox.items.length && !inbox.loading && (
          <Flexbox
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap="1rem"
            mt="2rem"
          >
            <IconBell
              size={42}
              stroke={1.5}
              color={buildPassTheme.colour.secondary._300}
            />
            <Text variant="label02" align="center" secondary="_400">
              Nice work! You&apos;re all caught up!
            </Text>
          </Flexbox>
        )}

        {!!inbox.items.length && inbox.items.length < inbox.totalCount && (
          <Flexbox justifyContent="center" mt="1rem" mb="1rem">
            <Button
              onClick={() => inbox.feedClient.fetchNextPage()}
              variant="secondary-text"
              size="small"
            >
              View more
            </Button>
          </Flexbox>
        )}
      </div>
    </ResponsiveDrawer>
  )
}
